import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    // SEC-003: CSRF defense. Browsers cannot set this header on simple HTML form
    // submissions, and setting it via fetch/XHR from a cross-site origin triggers
    // a CORS preflight that our credentialed allowlist rejects.
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Multi-tenant routing: Emergent's ingress rewrites Host/X-Forwarded-Host to
// the internal cluster hostname, so the backend cannot see the real public
// domain the browser used. We send it explicitly on every request so tenant
// resolution works for custom domains across dual deployments.
api.interceptors.request.use((config) => {
  try {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (host) {
      config.headers = config.headers || {};
      config.headers["X-Client-Host"] = host;
    }
  } catch (_) {}
  return config;
});

// In-flight request counter so pull-to-refresh knows when the reload has finished.
let inflight = 0;
api.interceptors.request.use((config) => { inflight += 1; return config; });
api.interceptors.response.use(
  (r) => { inflight = Math.max(0, inflight - 1); return r; },
  (e) => { inflight = Math.max(0, inflight - 1); return Promise.reject(e); },
);
export const waitForIdle = async (timeoutMs = 8000) => {
  const start = Date.now();
  await new Promise((r) => setTimeout(r, 150));
  while (inflight > 0 && Date.now() - start < timeoutMs) await new Promise((r) => setTimeout(r, 100));
};

export const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) {
    const f = d[0] || {};
    const field = (f.loc || []).slice(-1)[0];
    return field ? `${String(field).replace(/_/g, " ")}: ${(f.msg || "invalid").replace(/^Value error, /, "")}` : fallback;
  }
  return typeof d === "string" ? d : fallback;
};

export default api;

export const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

export const fmtDate = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

export const CYCLE_LABEL = {
  monthly: "Monthly", quarterly: "Quarterly", half_yearly: "Half-Yearly", yearly: "Yearly",
};

export const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  paused: "bg-orange-100 text-orange-800 border-orange-200",
  pending: "bg-amber-100 text-amber-900 border-amber-200",
  partial: "bg-blue-100 text-blue-800 border-blue-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
  transferred: "bg-stone-200 text-stone-700 border-stone-300",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  inactive: "bg-stone-200 text-stone-700 border-stone-300",
};

// When a blob response fails, axios surfaces the error body as a Blob rather
// than parsed JSON. This reads it as text and extracts the FastAPI `detail`
// so toasts show the real crash reason instead of a generic "download failed".
export const parseBlobError = async (err) => {
  try {
    const blob = err?.response?.data;
    if (!blob || typeof blob.text !== "function") return err?.message || "Download failed";
    const txt = await blob.text();
    try { return JSON.parse(txt).detail || txt || "Download failed"; }
    catch { return txt || "Download failed"; }
  } catch { return "Download failed"; }
};

export const downloadInvoicePdf = async (invoice) => {
  const r = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(invoice.invoice_no || "invoice").replace(/\//g, "_")}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export const downloadReceiptPdf = async (payment) => {
  const r = await api.get(`/payments/${payment.id}/receipt.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  const safe = (payment.invoice_no || payment.id || "receipt").replace(/\//g, "_");
  a.download = `receipt_${safe}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};